import { mapDayOfTheWeek } from '@/api/mappers/mapDayOfTheWeek';
import { toOrderPredictionAlgorithm } from '@/api/mappers/mapOrderPrediction';
import { toApiRetailer, toRetailer } from '@/api/mappers/mapRetailer';
import { toApiStatus, toStatus } from '@/api/mappers/mapStatus';
import {
  Store as ApiStore,
  StoreAddress as ApiStoreAddress,
  StoreOrderPrediction as ApiStoreOrderPrediction,
  StoreReceivingWindow as ApiStoreReceivingWindow,
  StoreCreateRequest,
  StoreUpdateRequest,
} from '@/gen/api';
import {
  StoreAddress as StoreAddressModel,
  StoreAddressType,
  StoreCreate as StoreCreateModel,
  Store as StoreModel,
  StoreOrderPrediction as StoreOrderPredictionModel,
  StoreReceivingWindow as StoreReceivingWindowModel,
  StoreUpdate as StoreUpdateModel,
} from '@/models/Store';
import { formatTime, TimeImpl } from '@/models/Time';

const toStoreAddressType = (value: string): StoreAddressType => {
  switch (value.toLowerCase()) {
    case String(StoreAddressType.BUSINESS):
      return StoreAddressType.BUSINESS;
    case String(StoreAddressType.BILLING):
      return StoreAddressType.BILLING;
    case String(StoreAddressType.DELIVERY):
      return StoreAddressType.DELIVERY;
  }
  return StoreAddressType.UNKNOWN;
};

export const toStoreAddress = (apiAddress: ApiStoreAddress): StoreAddressModel => {
  return {
    ...apiAddress,
    type: toStoreAddressType(apiAddress.type),
  };
};

const toReceivingWindows = (
  apiStoreReceivingWindows: ApiStoreReceivingWindow[],
): StoreReceivingWindowModel[] => {
  const receivingWindows: (StoreReceivingWindowModel | undefined)[] = apiStoreReceivingWindows.map(
    (apiStoreReceivingWindow) => {
      const day = mapDayOfTheWeek(apiStoreReceivingWindow.day);
      const startTime = TimeImpl.fromString(apiStoreReceivingWindow.startTime);
      const endTime = TimeImpl.fromString(apiStoreReceivingWindow.endTime);

      if (!day || !startTime || !endTime) {
        return undefined;
      }

      return {
        day: day,
        startTime: startTime,
        endTime: endTime,
      };
    },
  );

  return receivingWindows.filter((item) => item !== undefined) as StoreReceivingWindowModel[];
};

const toApiReceivingWindow = (
  receivingWindow: StoreReceivingWindowModel,
): ApiStoreReceivingWindow => {
  return {
    ...receivingWindow,
    startTime: formatTime(receivingWindow.startTime),
    endTime: formatTime(receivingWindow.endTime),
  };
};

export const toStore = (apiStore: ApiStore): StoreModel => {
  return {
    ...apiStore,
    id: apiStore.id as string,
    retailerId: apiStore.retailerId,
    customerCode: apiStore.customerCode || '',
    contactCountryCode: apiStore.contactCountryCode || '',
    contactNumber: apiStore.contactNumber || '',
    moq: apiStore.moq || 0,
    status: toStatus(apiStore.status),
    addresses: apiStore.addresses.map(toStoreAddress),
    createdAt: apiStore.createdAt || new Date(0),
    receivingWindows: toReceivingWindows(apiStore.receivingWindows),
    retailer: apiStore.retailer ? toRetailer(apiStore.retailer) : undefined,
    orderPrediction: {
      enabled: apiStore.orderPrediction.enabled,
      algorithm: apiStore.orderPrediction?.algorithm
        ? toOrderPredictionAlgorithm(apiStore.orderPrediction?.algorithm)
        : undefined,
    },
  };
};

export const toApiStore = (store: StoreModel): ApiStore => {
  return {
    ...store,
    retailerId: store.retailerId,
    status: toApiStatus(store.status),
    receivingWindows: store.receivingWindows.map(toApiReceivingWindow),
    retailer: store.retailer ? toApiRetailer(store.retailer) : undefined,
  };
};

const toApiStoreOrderPrediction = (
  orderPrediction: StoreOrderPredictionModel,
): ApiStoreOrderPrediction => ({
  enabled: orderPrediction.enabled,
  algorithm:
    orderPrediction.enabled && orderPrediction.algorithm ? orderPrediction.algorithm : undefined,
});

export const toApiStoreCreateRequest = (store: StoreCreateModel): StoreCreateRequest => {
  return {
    ...store,
    retailerId: store.retailerId,
    receivingWindows: store.receivingWindows.map(toApiReceivingWindow),
    orderPrediction: toApiStoreOrderPrediction(store.orderPrediction),
  };
};

export const toApiStoreUpdateRequest = (store: StoreUpdateModel): StoreUpdateRequest => {
  return {
    ...store,
    retailerId: store.retailerId,
    status: toApiStatus(store.status),
    receivingWindows: store.receivingWindows.map(toApiReceivingWindow),
    orderPrediction: toApiStoreOrderPrediction(store.orderPrediction),
  };
};
